import { supabase } from '@/utils/supabase';
import { createResumableUploadTask } from './resumableUpload';
import { InvestorDocument, DocumentCategory } from '@/types/investor';
import { createComponentLogger } from '@/utils/logger';
import { hiddenRolesFromAllowedRoles } from '@/utils/investor/documentVisibility';
import { resolveOriginalDocumentFileName } from '@/utils/investor/linkedDocumentLabel';

const log = createComponentLogger('DocumentService', 'document-management');

export interface DocumentMetadata {
  investorId: string;
  organizationId: string;
  documentType: 'wholesale' | 'correspondent';
  category: DocumentCategory;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  notes?: string;
  subType?: string;    // For Credit Guidelines sub-categorization
  fileHash?: string;   // SHA-256 hash for duplicate detection
  version?: number;    // Pre-computed version (avoids duplicate computation)
  effectiveDate?: string; // Effective date for Credit Guidelines, Credit Matrix, Seller Guide
  displayName?: string | null; // Explicit custom title; NULL uses the original file name.
  // Manage Investors v2 gating metadata (UI-level only)
  hiddenRoles?: string[];
  // Legacy compatibility only. New writes should pass hiddenRoles.
  allowedRoles?: string[];
  linkedTabs?: ('processing' | 'closing')[];
}

interface DocumentMetadataPatch {
  displayName?: string | null;
  notes?: string | null;
  effectiveDate?: string | null;
  linkedTabs?: ('processing' | 'closing')[];
  hiddenRoles?: string[];
}

function mapHiddenRoles(row: { hidden_roles?: string[] | null; allowed_roles?: string[] | null }): string[] | undefined {
  if (Array.isArray(row.hidden_roles)) {
    return row.hidden_roles;
  }
  return hiddenRolesFromAllowedRoles(row.allowed_roles);
}

interface InvestorDocumentFileType {
  contentType: string;
  compatibleTypes: string[];
  kind: 'document' | 'image' | 'spreadsheet';
  previewable: boolean;
}

const INVESTOR_DOCUMENT_FILE_TYPES: Record<string, InvestorDocumentFileType> = {
  pdf: {
    contentType: 'application/pdf',
    compatibleTypes: ['application/pdf'],
    kind: 'document',
    previewable: true
  },
  doc: {
    contentType: 'application/msword',
    compatibleTypes: ['application/msword', 'application/doc', 'application/vnd.ms-word'],
    kind: 'document',
    previewable: false
  },
  docx: {
    contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compatibleTypes: [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/zip'
    ],
    kind: 'document',
    previewable: false
  },
  xls: {
    contentType: 'application/vnd.ms-excel',
    compatibleTypes: [
      'application/vnd.ms-excel',
      'application/excel',
      'application/x-excel',
      'application/x-msexcel',
      'application/xls'
    ],
    kind: 'spreadsheet',
    previewable: false
  },
  xlsx: {
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    compatibleTypes: [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/zip'
    ],
    kind: 'spreadsheet',
    previewable: false
  },
  xlsm: {
    contentType: 'application/vnd.ms-excel.sheet.macroEnabled.12',
    compatibleTypes: [
      'application/vnd.ms-excel.sheet.macroenabled.12',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/zip'
    ],
    kind: 'spreadsheet',
    previewable: false
  },
  csv: {
    contentType: 'text/csv',
    compatibleTypes: ['text/csv', 'text/plain', 'application/csv', 'application/vnd.ms-excel'],
    kind: 'spreadsheet',
    previewable: false
  },
  jpg: {
    contentType: 'image/jpeg',
    compatibleTypes: ['image/jpeg', 'image/jpg'],
    kind: 'image',
    previewable: true
  },
  jpeg: {
    contentType: 'image/jpeg',
    compatibleTypes: ['image/jpeg', 'image/jpg'],
    kind: 'image',
    previewable: true
  },
  png: {
    contentType: 'image/png',
    compatibleTypes: ['image/png'],
    kind: 'image',
    previewable: true
  }
};

const GENERIC_BROWSER_MIME_TYPES = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream'
]);

const getFileExtension = (fileName: string): string => {
  const baseName = fileName.split(/[\\/]/).pop() ?? '';
  const finalDotIndex = baseName.lastIndexOf('.');
  return finalDotIndex > 0 ? baseName.slice(finalDotIndex + 1).toLowerCase() : '';
};

const getDocumentFileType = (fileName: string): InvestorDocumentFileType | undefined =>
  INVESTOR_DOCUMENT_FILE_TYPES[getFileExtension(fileName)];

const normalizeMimeType = (mimeType: string): string =>
  mimeType.split(';', 1)[0].trim().toLowerCase();

const resolveCanonicalContentType = (fileName: string, fallbackMimeType = ''): string =>
  getDocumentFileType(fileName)?.contentType || normalizeMimeType(fallbackMimeType) || 'application/octet-stream';

const RESERVED_FILENAME_CHARACTERS = new Set(['/', '\\', ':', '*', '?', '"', '<', '>', '|']);

const sanitizeDownloadFileName = (fileName: string): string => {
  const sanitized = Array.from(fileName, (character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint < 32 || codePoint === 127 || RESERVED_FILENAME_CHARACTERS.has(character)
      ? '_'
      : character;
  }).join('');

  return sanitized.replace(/[. ]+$/, '').trim() || 'document';
};

export class DocumentService {
  private static readonly BUCKET_NAME = 'documents';
  static readonly MAX_FILE_SIZE_MB = 20;
  static readonly FILE_INPUT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.jpg,.jpeg,.png';
  static readonly ACCEPTED_FORMATS_LABEL = 'PDF, Word (DOC/DOCX), Excel (XLS/XLSX/XLSM), CSV, JPG, PNG';
  private static readonly MAX_FILE_SIZE = this.MAX_FILE_SIZE_MB * 1024 * 1024;

  static getFileKind(fileName: string): InvestorDocumentFileType['kind'] {
    return getDocumentFileType(fileName)?.kind ?? 'document';
  }

  static isPreviewable(document: Pick<InvestorDocument, 'originalFileName' | 'mimeType'>): boolean {
    const fileType = getDocumentFileType(document.originalFileName);
    if (fileType) {
      return fileType.previewable;
    }

    const mimeType = normalizeMimeType(document.mimeType);
    return mimeType === 'application/pdf' || mimeType === 'image/jpeg' || mimeType === 'image/png';
  }

  static getCanonicalContentType(fileName: string, fallbackMimeType = ''): string {
    return resolveCanonicalContentType(fileName, fallbackMimeType);
  }

  static getDownloadUrl(document: Pick<
    InvestorDocument,
    'fileUrl' | 'originalFileName' | 'storageBucket' | 'storagePath'
  >): string {
    const originalFileName = resolveOriginalDocumentFileName(document);
    const downloadFileName = sanitizeDownloadFileName(originalFileName);

    if (document.storagePath) {
      const { data } = supabase.storage
        .from(document.storageBucket || this.BUCKET_NAME)
        .getPublicUrl(document.storagePath, { download: downloadFileName });
      return data.publicUrl;
    }

    try {
      const fallbackUrl = new URL(document.fileUrl);
      fallbackUrl.searchParams.set('download', downloadFileName);
      return fallbackUrl.toString();
    } catch {
      return document.fileUrl;
    }
  }

  /**
   * Generate storage path for a document
   */
  private static generateStoragePath(
    organizationId: string,
    investorId: string,
    documentType: string,
    category: string,
    version: number,
    fileName: string
  ): string {
    // Clean the category name for use in path (remove spaces, special chars)
    const cleanCategory = category.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const timestamp = Date.now();
    const fileExt = getFileExtension(fileName) || 'pdf';

    return `investors/${organizationId}/${investorId}/${documentType}/${cleanCategory}/v${version}_${timestamp}.${fileExt}`;
  }

  /**
   * Validate file before upload
   */
  static validateFile(file: File): string | null {
    if (!Number.isSafeInteger(file.size) || file.size <= 0) {
      return 'The file is empty or its size is invalid';
    }
    if (file.size > this.MAX_FILE_SIZE) {
      return `File size exceeds maximum allowed size of ${this.MAX_FILE_SIZE_MB}MB`;
    }

    const fileType = getDocumentFileType(file.name);
    if (!fileType) {
      return `File type not allowed. Please upload ${this.ACCEPTED_FORMATS_LABEL}`;
    }

    const browserMimeType = normalizeMimeType(file.type);
    const compatibleTypes = fileType.compatibleTypes.map(normalizeMimeType);
    if (!GENERIC_BROWSER_MIME_TYPES.has(browserMimeType) && !compatibleTypes.includes(browserMimeType)) {
      return `File type does not match the ${getFileExtension(file.name).toUpperCase()} extension`;
    }

    return null;
  }

  /**
   * Upload a document to Supabase storage
   */
  static async uploadDocument(
    file: File,
    metadata: DocumentMetadata,
    options?: { documentId: string; onProgress: (percentage: number) => void }
  ): Promise<{ url: string; path: string }> {
    try {
      log.info('Starting document upload', {
        fileName: file.name,
        fileSize: file.size,
        category: metadata.category,
        investorId: metadata.investorId
      });

      // Validate the file
      const validationError = this.validateFile(file);
      if (validationError) {
        throw new Error(validationError);
      }

      // Use pre-computed version if provided, otherwise compute it
      const version = metadata.version ?? await this.getNextVersionNumber(
        metadata.investorId,
        metadata.documentType,
        metadata.category,
        metadata.subType
      );

      // Generate the storage path
      const storagePath = options
        ? `investors/${metadata.organizationId}/${metadata.investorId}/${metadata.documentType}/${options.documentId}.${getFileExtension(file.name)}`
        : this.generateStoragePath(
        metadata.organizationId,
        metadata.investorId,
        metadata.documentType,
        metadata.category,
        version,
        file.name
      );
      const contentType = resolveCanonicalContentType(file.name, file.type);

      if (options) {
        const { data: { session }, error: authError } = await supabase.auth.getSession();
        if (authError || !session) {
          throw new Error('Sign in again before uploading.');
        }
        const endpoint = new URL('/storage/v1/upload/resumable', import.meta.env.VITE_SUPABASE_URL).href;
        // The path is this row's own UUID, so a retry after a lost acknowledgement
        // may overwrite it: the same file is sent again to the same object.
        const task = createResumableUploadTask({
          file, endpoint,
          headers: {
            authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            'x-upsert': 'true'
          },
          bucket: this.BUCKET_NAME, path: storagePath, contentType,
          fingerprintNamespace: 'investor-document',
          onProgress: options.onProgress
        });
        await task.start();
        const { data: publicData } = supabase.storage.from(this.BUCKET_NAME).getPublicUrl(storagePath);
        return { url: publicData.publicUrl, path: storagePath };
      }

      // Upload to Supabase storage
      const { data, error } = await supabase.storage
        .from(this.BUCKET_NAME)
        .upload(storagePath, file, {
          contentType,
          upsert: false // Don't overwrite existing files
        });

      if (error) {
        log.error('Failed to upload document to storage', { error, storagePath });
        throw new Error(`Failed to upload document: ${error.message}`);
      }

      // Get the public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from(this.BUCKET_NAME)
        .getPublicUrl(data.path);

      log.info('Document uploaded successfully', {
        path: data.path,
        version,
        category: metadata.category
      });

      return {
        url: urlData.publicUrl,
        path: data.path
      };
    } catch (error) {
      log.error('Document upload failed', { error });
      throw error;
    }
  }

  /**
   * Get the next version number for a document category
   * For Credit Guidelines, versioning is scoped by sub_type
   */
  static async getNextVersionNumber(
    investorId: string,
    documentType: string,
    category: string,
    subType?: string  // Required for Credit Guidelines
  ): Promise<number> {
    try {
      let query = supabase
        .from('investor_documents')
        .select('version')
        .eq('investor_id', investorId)
        .eq('document_type', documentType)
        .eq('category', category);

      // AUDIT FIX: Include sub_type for Credit Guidelines and Credit Matrix versioning
      if ((category === 'Credit Guidelines' || category === 'Credit Matrix') && subType) {
        query = query.eq('sub_type', subType);
      }

      const { data, error } = await query
        .order('version', { ascending: false })
        .limit(1);

      if (error) {
        log.error('Failed to get version number', { error });
        return 1; // Default to version 1 on error
      }

      // If no documents exist, start with version 1
      if (!data || data.length === 0) {
        return 1;
      }

      return (data[0]?.version || 0) + 1;
    } catch (error) {
      log.error('Failed to get next version number', { error });
      return 1;
    }
  }

  /** Item 10: next version within a document lineage (group). Used for replace storage paths. */
  static async getNextVersionForGroup(documentGroupId: string): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('investor_documents')
        .select('version')
        .eq('document_group_id', documentGroupId)
        .order('version', { ascending: false })
        .limit(1);
      if (error) {
        log.error('Failed to get next group version', { error });
        return 1;
      }
      return (data?.[0]?.version || 0) + 1;
    } catch (error) {
      log.error('Failed to get next group version', { error });
      return 1;
    }
  }

  /**
   * Item 10: atomic Replace — archive the lineage's current active row and insert the new
   * version as active in one DB transaction (via the replace_investor_document RPC). Never
   * leaves a lineage with no active row, unlike a client-side archive-then-insert.
   */
  static async replaceDocumentViaRpc(
    documentGroupId: string,
    metadata: DocumentMetadata,
    storageUrl: string,
    storagePath: string
  ): Promise<InvestorDocument> {
    const displayName = metadata.displayName?.trim() || null;
    const mimeType = resolveCanonicalContentType(metadata.originalFileName, metadata.mimeType);

    const { data, error } = await supabase.rpc('replace_investor_document', {
      p_group_id: documentGroupId,
      p_investor_id: metadata.investorId,
      p_organization_id: metadata.organizationId,
      p_document_type: metadata.documentType,
      p_category: metadata.category,
      p_original_file_name: metadata.originalFileName,
      p_display_name: displayName,
      p_file_url: storageUrl,
      p_file_size: metadata.fileSize,
      p_mime_type: mimeType,
      p_notes: metadata.notes ?? null,
      p_uploaded_by: metadata.uploadedBy ?? null,
      p_storage_path: storagePath,
      p_storage_bucket: this.BUCKET_NAME,
      p_extraction_status: mimeType === 'application/pdf' ? 'pending' : 'skipped',
      p_sub_type: metadata.subType ?? null,
      p_file_hash: metadata.fileHash ?? null,
      p_effective_date: metadata.effectiveDate || null,
      p_hidden_roles: metadata.hiddenRoles ?? hiddenRolesFromAllowedRoles(metadata.allowedRoles),
      p_allowed_roles: metadata.allowedRoles ?? [],
      p_linked_tabs: metadata.linkedTabs ?? []
    });

    if (error) {
      log.error('Failed to replace document via rpc', { error });
      throw new Error(`Failed to replace document: ${error.message}`);
    }

    const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown>;
    return {
      id: row.id as string,
      investorId: row.investor_id as string,
      documentType: row.document_type as 'wholesale' | 'correspondent',
      category: row.category as DocumentCategory,
      originalFileName: row.original_file_name as string,
      displayName: (row.display_name as string | null) ?? null,
      fileUrl: row.file_url as string,
      fileSize: row.file_size as number,
      mimeType: row.mime_type as string,
      uploadedAt: row.uploaded_at as string,
      uploadedBy: row.uploaded_by as string,
      isActive: row.is_active as boolean,
      version: row.version as number,
      documentGroupId: row.document_group_id as string,
      notes: (row.notes as string) ?? undefined,
      storagePath: (row.storage_path as string) ?? undefined,
      storageBucket: (row.storage_bucket as string) ?? undefined,
      extractionStatus: row.extraction_status as InvestorDocument['extractionStatus'],
      subType: (row.sub_type as string) ?? undefined,
      fileHash: (row.file_hash as string) ?? undefined,
      effectiveDate: (row.effective_date as string) ?? undefined,
      hiddenRoles: (row.hidden_roles as string[]) ?? undefined,
      allowedRoles: (row.allowed_roles as string[]) ?? undefined,
      linkedTabs: (row.linked_tabs as ('processing' | 'closing')[]) ?? undefined
    };
  }

  /**
   * Archive existing active document of the same category
   * For Credit Guidelines, archiving is scoped by sub_type
   * For Credit Matrix, skip auto-archiving (multiple can coexist, use explicit replace)
   */
  static async archiveExistingDocument(
    investorId: string,
    documentType: string,
    category: string,
    organizationId: string,
    subType?: string  // Required for Credit Guidelines
  ): Promise<void> {
    try {
      // Credit Matrix allows multiple active documents - skip auto-archiving
      // Users can explicitly replace using the Replace button
      if (category === 'Credit Matrix') {
        log.info('Skipping auto-archive for Credit Matrix - multiple documents allowed', {
          investorId,
          documentType,
          category
        });
        return;
      }

      log.info('Archiving existing document', {
        investorId,
        documentType,
        category,
        subType
      });

      let query = supabase
        .from('investor_documents')
        .update({ is_active: false })
        .eq('investor_id', investorId)
        .eq('organization_id', organizationId)
        .eq('document_type', documentType)
        .eq('category', category)
        .eq('is_active', true);

      // AUDIT FIX: Include sub_type for Credit Guidelines archiving
      if (category === 'Credit Guidelines' && subType) {
        query = query.eq('sub_type', subType);
      }

      const { error } = await query;

      if (error) {
        log.error('Failed to archive existing document', { error });
        throw new Error(`Failed to archive existing document: ${error.message}`);
      }

      log.info('Existing document archived successfully');
    } catch (error) {
      log.error('Document archiving failed', { error });
      throw error;
    }
  }

  /**
   * Save document metadata to database
   */
  static async saveDocumentMetadata(
    metadata: DocumentMetadata,
    storageUrl: string,
    storagePath: string,
    version: number,
    documentId?: string
  ): Promise<InvestorDocument> {
    try {
      // Item 10: a fresh upload starts a NEW lineage (document_group_id defaults to a fresh
      // uuid) and does NOT archive same-category siblings — multiple documents of one type
      // coexist. Replacement happens only via replaceInvestorDocument (the per-row Replace).

      const displayName = metadata.displayName?.trim() || null;
      const mimeType = resolveCanonicalContentType(metadata.originalFileName, metadata.mimeType);

      // Log the data being inserted for debugging
      const insertData = {
        ...(documentId ? { id: documentId } : {}),
        investor_id: metadata.investorId,
        organization_id: metadata.organizationId,
        document_type: metadata.documentType,
        category: metadata.category,
        original_file_name: metadata.originalFileName,
        display_name: displayName,
        file_url: storageUrl,
        file_size: metadata.fileSize,
        mime_type: mimeType,
        version,
        is_active: true,
        notes: metadata.notes,
        uploaded_by: metadata.uploadedBy,
        // Storage path for worker access
        storage_path: storagePath,
        storage_bucket: this.BUCKET_NAME,
        // Set extraction status based on file type (only PDFs can be processed by Docling)
        extraction_status: mimeType === 'application/pdf' ? 'pending' : 'skipped',
        // Credit Guidelines sub-type and file hash
        sub_type: metadata.subType || null,
        file_hash: metadata.fileHash || null,
        effective_date: metadata.effectiveDate || null,
        // Manage Investors v2 gating metadata
        hidden_roles: metadata.hiddenRoles ?? hiddenRolesFromAllowedRoles(metadata.allowedRoles),
        allowed_roles: metadata.allowedRoles ?? [],
        linked_tabs: metadata.linkedTabs ?? []
      };

      log.info('Attempting to save document metadata', { insertData });

      // Same ID on every attempt. Read before insert and after ambiguous failures.
      // A mismatching row is never overwritten or accepted as this upload.
      const readSaved = async () => {
        if (!documentId) {
          return null;
        }
        const result = await supabase.from('investor_documents').select('*')
          .eq('id', documentId).eq('organization_id', metadata.organizationId).maybeSingle();
        if (result.error) {
          throw new Error(`Cannot verify saved document: ${result.error.message}`);
        }
        if (result.data) {
          const mutableFields = new Set(['extraction_status']);
          for (const [key, expected] of Object.entries(insertData)) {
            if (mutableFields.has(key)) {
              continue;
            }
            const actual = result.data[key];
            const normalize = (value: unknown) => Array.isArray(value)
              ? JSON.stringify([...value].sort()) : value ?? null;
            if (normalize(actual) !== normalize(expected)) {
              throw new Error('Saved document identity or metadata differs. Refresh the document list before continuing.');
            }
          }
        }
        return result.data;
      };
      let data = await readSaved();
      if (!data) {
        try {
          const result = await supabase.from('investor_documents').insert(insertData).select().single();
          if (result.error) {
            throw new Error(`Failed to save document metadata: ${result.error.message}`);
          }
          if (!result.data) {
            throw new Error('Database did not confirm the document row.');
          }
          data = result.data;
        } catch (error) {
          data = await readSaved();
          if (!data) {
            throw error;
          }
        }
      }

      // Convert database record to InvestorDocument type
      const document: InvestorDocument = {
        id: data.id,
        investorId: data.investor_id,
        documentType: data.document_type as 'wholesale' | 'correspondent',
        category: data.category as DocumentCategory,
        originalFileName: data.original_file_name,
        displayName: data.display_name,
        fileUrl: data.file_url,
        fileSize: data.file_size,
        mimeType: data.mime_type,
        uploadedAt: data.uploaded_at,
        uploadedBy: data.uploaded_by,
        isActive: data.is_active,
        version: data.version,
        documentGroupId: data.document_group_id,
        notes: data.notes,
        // Storage fields
        storagePath: data.storage_path,
        storageBucket: data.storage_bucket,
        // Extraction fields (will be null on initial save)
        extractionStatus: data.extraction_status,
        // Credit Guidelines sub-type and file hash
        subType: data.sub_type,
        fileHash: data.file_hash,
        effectiveDate: data.effective_date,
        // Manage Investors v2 gating metadata
        hiddenRoles: mapHiddenRoles(data),
        allowedRoles: data.allowed_roles ?? undefined,
        linkedTabs: (data.linked_tabs ?? undefined) as ('processing' | 'closing')[] | undefined
      };

      log.info('Document metadata saved successfully', { documentId: document.id });
      return document;
    } catch (error) {
      log.error('Failed to save document metadata', { error });
      throw error;
    }
  }

  /**
   * Delete a document from storage and database
   */
  static async deleteDocument(
    documentId: string,
    organizationId: string
  ): Promise<void> {
    try {
      log.info('Deleting document', { documentId });

      // Get document info first
      const { data: docData, error: fetchError } = await supabase
        .from('investor_documents')
        .select('file_url')
        .eq('id', documentId)
        .eq('organization_id', organizationId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch document: ${fetchError.message}`);
      }

      // Extract storage path from URL
      const urlParts = docData.file_url.split('/');
      const storagePath = urlParts.slice(urlParts.indexOf('documents') + 1).join('/');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from(this.BUCKET_NAME)
        .remove([storagePath]);

      if (storageError) {
        log.error('Failed to delete from storage', { storageError });
        // Continue to delete from database even if storage deletion fails
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('investor_documents')
        .delete()
        .eq('id', documentId)
        .eq('organization_id', organizationId);

      if (dbError) {
        throw new Error(`Failed to delete document from database: ${dbError.message}`);
      }

      log.info('Document deleted successfully', { documentId });
    } catch (error) {
      log.error('Document deletion failed', { error });
      throw error;
    }
  }

  /**
   * Get all documents for an investor
   */
  static async getInvestorDocuments(
    investorId: string,
    documentType?: 'wholesale' | 'correspondent',
    activeOnly: boolean = true
  ): Promise<InvestorDocument[]> {
    try {
      let query = supabase
        .from('investor_documents')
        .select('*')
        .eq('investor_id', investorId);

      if (documentType) {
        query = query.eq('document_type', documentType);
      }

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      query = query.order('uploaded_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        log.error('Failed to fetch investor documents', { error });
        throw new Error(`Failed to fetch documents: ${error.message}`);
      }

      // Convert database records to InvestorDocument type
      const documents: InvestorDocument[] = (data || []).map(doc => ({
        id: doc.id,
        investorId: doc.investor_id,
        documentType: doc.document_type as 'wholesale' | 'correspondent',
        category: doc.category as DocumentCategory,
        originalFileName: doc.original_file_name,
        displayName: doc.display_name,
        fileUrl: doc.file_url,
        fileSize: doc.file_size,
        mimeType: doc.mime_type,
        uploadedAt: doc.uploaded_at,
        uploadedBy: doc.uploaded_by,
        isActive: doc.is_active,
        version: doc.version,
        documentGroupId: doc.document_group_id,
        notes: doc.notes,
        // Storage fields
        storagePath: doc.storage_path,
        storageBucket: doc.storage_bucket,
        // Extraction fields
        content: doc.content,
        extractionStatus: doc.extraction_status,
        extractionError: doc.extraction_error,
        extractionStartedAt: doc.extraction_started_at,
        extractedAt: doc.extracted_at,
        totalPages: doc.total_pages,
        extractionTimeMs: doc.extraction_time_ms,
        // Credit Guidelines sub-type and file hash
        subType: doc.sub_type,
        fileHash: doc.file_hash,
        effectiveDate: doc.effective_date,
        // Manage Investors v2 gating metadata
        hiddenRoles: mapHiddenRoles(doc),
        allowedRoles: doc.allowed_roles ?? undefined,
        linkedTabs: (doc.linked_tabs ?? undefined) as ('processing' | 'closing')[] | undefined
      }));

      return documents;
    } catch (error) {
      log.error('Failed to get investor documents', { error });
      throw error;
    }
  }

  /**
   * Archive a document (set is_active to false)
   */
  static async archiveDocument(
    documentId: string,
    organizationId: string
  ): Promise<void> {
    try {
      log.info('Archiving document', { documentId });

      const { error } = await supabase
        .from('investor_documents')
        .update({ is_active: false })
        .eq('id', documentId)
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(`Failed to archive document: ${error.message}`);
      }

      log.info('Document archived successfully', { documentId });
    } catch (error) {
      log.error('Document archiving failed', { error });
      throw error;
    }
  }

  /**
   * Update or clear a document's custom display name
   */
  static async updateDocumentDisplayName(
    documentId: string,
    displayName: string | null,
    organizationId: string
  ): Promise<void> {
    try {
      log.info('Updating document display name', { documentId, displayName });

      await DocumentService.updateDocumentMetadata(documentId, organizationId, {
        displayName: displayName?.trim() || null
      });

      log.info('Document display name updated successfully', { documentId });
    } catch (error) {
      log.error('Document display name update failed', { error });
      throw error;
    }
  }

  /**
   * Update a document's effective date
   */
  static async updateDocumentEffectiveDate(
    documentId: string,
    effectiveDate: string | null,
    organizationId: string
  ): Promise<void> {
    try {
      log.info('Updating document effective date', { documentId, effectiveDate });

      const { error } = await supabase
        .from('investor_documents')
        .update({ effective_date: effectiveDate || null })
        .eq('id', documentId)
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(`Failed to update document effective date: ${error.message}`);
      }

      log.info('Document effective date updated successfully', { documentId });
    } catch (error) {
      log.error('Document effective date update failed', { error });
      throw error;
    }
  }

  /**
   * Manage Investors v2: set which slide-over tabs (processing/closing) surface a document.
   */
  static async updateDocumentLinkedTabs(
    documentId: string,
    linkedTabs: ('processing' | 'closing')[],
    organizationId: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('investor_documents')
        .update({ linked_tabs: linkedTabs })
        .eq('id', documentId)
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(`Failed to update document linked tabs: ${error.message}`);
      }
      log.info('Document linked tabs updated', { documentId, linkedTabs });
    } catch (error) {
      log.error('Document linked tabs update failed', { error });
      throw error;
    }
  }

  /**
   * Manage Investors v2: set the UI-level role visibility filter for a document.
   * NOTE: UI/service filtering only — not enforced access control while the bucket is public.
   */
  static async updateDocumentAllowedRoles(
    documentId: string,
    allowedRoles: string[],
    organizationId: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('investor_documents')
        .update({
          allowed_roles: allowedRoles,
          hidden_roles: hiddenRolesFromAllowedRoles(allowedRoles)
        })
        .eq('id', documentId)
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(`Failed to update document allowed roles: ${error.message}`);
      }
      log.info('Document allowed roles updated', { documentId, allowedRoles });
    } catch (error) {
      log.error('Document allowed roles update failed', { error });
      throw error;
    }
  }

  /**
   * Manage Investors v2: set UI-level role exclusions for a document.
   * NOTE: UI/service filtering only — not enforced access control while the bucket is public.
   */
  static async updateDocumentHiddenRoles(
    documentId: string,
    hiddenRoles: string[],
    organizationId: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('investor_documents')
        .update({ hidden_roles: hiddenRoles, allowed_roles: [] })
        .eq('id', documentId)
        .eq('organization_id', organizationId);

      if (error) {
        throw new Error(`Failed to update document hidden roles: ${error.message}`);
      }
      log.info('Document hidden roles updated', { documentId, hiddenRoles });
    } catch (error) {
      log.error('Document hidden roles update failed', { error });
      throw error;
    }
  }

  /**
   * Manage Investors v2: update editable document metadata without changing
   * category/type/version lineage or stored file identity.
   */
  static async updateDocumentMetadata(
    documentId: string,
    organizationId: string,
    patch: DocumentMetadataPatch
  ): Promise<void> {
    try {
      const update: Record<string, unknown> = {};
      if (patch.displayName !== undefined) {
        update.display_name = patch.displayName;
      }
      if (patch.notes !== undefined) {
        update.notes = patch.notes;
      }
      if (patch.effectiveDate !== undefined) {
        update.effective_date = patch.effectiveDate || null;
      }
      if (patch.linkedTabs !== undefined) {
        update.linked_tabs = patch.linkedTabs;
      }
      if (patch.hiddenRoles !== undefined) {
        update.hidden_roles = patch.hiddenRoles;
        update.allowed_roles = [];
      }

      if (Object.keys(update).length === 0) {
        return;
      }

      const { data, error } = await supabase
        .from('investor_documents')
        .update(update)
        .eq('id', documentId)
        .eq('organization_id', organizationId)
        .select('id, display_name')
        .single();

      if (error) {
        throw new Error(`Failed to update document metadata: ${error.message}`);
      }
      if (!data?.id) {
        throw new Error('Failed to update document metadata: database did not confirm the document row');
      }
      log.info('Document metadata updated', { documentId, fields: Object.keys(update) });
    } catch (error) {
      log.error('Document metadata update failed', { error });
      throw error;
    }
  }

  /**
   * Check for duplicate file by hash (scoped by category, document_type, and sub_type)
   */
  static async checkForDuplicate(
    investorId: string,
    fileHash: string,
    category: string,
    documentType: 'wholesale' | 'correspondent',
    subType?: string
  ): Promise<InvestorDocument | null> {
    try {
      let query = supabase
        .from('investor_documents')
        .select('*')
        .eq('investor_id', investorId)
        .eq('file_hash', fileHash)
        .eq('category', category)
        .eq('document_type', documentType)
        .eq('is_active', true);

      // For Credit Guidelines, also scope by sub_type
      if (category === 'Credit Guidelines' && subType) {
        query = query.eq('sub_type', subType);
      }

      const { data, error } = await query.order('uploaded_at', { ascending: false }).order('id').limit(1).maybeSingle();

      if (error) {
        log.error('Failed to check for duplicate', { error });
        throw error;
      }

      if (!data) {
        return null;
      }

      // Map to InvestorDocument
      return {
        id: data.id,
        investorId: data.investor_id,
        documentType: data.document_type as 'wholesale' | 'correspondent',
        category: data.category as DocumentCategory,
        originalFileName: data.original_file_name,
        displayName: data.display_name,
        fileUrl: data.file_url,
        fileSize: data.file_size,
        mimeType: data.mime_type,
        uploadedAt: data.uploaded_at,
        uploadedBy: data.uploaded_by,
        isActive: data.is_active,
        version: data.version,
        notes: data.notes,
        storagePath: data.storage_path,
        storageBucket: data.storage_bucket,
        subType: data.sub_type,
        fileHash: data.file_hash,
        effectiveDate: data.effective_date,
        hiddenRoles: mapHiddenRoles(data),
        allowedRoles: data.allowed_roles ?? undefined,
        linkedTabs: (data.linked_tabs ?? undefined) as ('processing' | 'closing')[] | undefined
      };
    } catch (error) {
      log.error('Failed to check for duplicate', { error });
      throw error;
    }
  }

  /**
   * Compute SHA-256 hash of a file for duplicate detection
   */
  static async computeFileHash(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Get all versions (active + archived) for a specific document lineage
   */
  static async getDocumentVersionHistory(
    documentGroupId: string
  ): Promise<InvestorDocument[]> {
    try {
      const query = supabase
        .from('investor_documents')
        .select('*')
        .eq('document_group_id', documentGroupId)
        .order('version', { ascending: false });

      const { data, error } = await query;

      if (error) {
        log.error('Failed to fetch version history', { error });
        throw new Error(`Failed to fetch version history: ${error.message}`);
      }

      return (data || []).map(doc => ({
        id: doc.id,
        investorId: doc.investor_id,
        documentType: doc.document_type as 'wholesale' | 'correspondent',
        category: doc.category as DocumentCategory,
        originalFileName: doc.original_file_name,
        displayName: doc.display_name,
        fileUrl: doc.file_url,
        fileSize: doc.file_size,
        mimeType: doc.mime_type,
        uploadedAt: doc.uploaded_at,
        uploadedBy: doc.uploaded_by,
        isActive: doc.is_active,
        version: doc.version,
        documentGroupId: doc.document_group_id,
        notes: doc.notes,
        storagePath: doc.storage_path,
        storageBucket: doc.storage_bucket,
        extractionStatus: doc.extraction_status,
        subType: doc.sub_type,
        fileHash: doc.file_hash,
        effectiveDate: doc.effective_date,
        hiddenRoles: mapHiddenRoles(doc),
        allowedRoles: doc.allowed_roles ?? undefined,
        linkedTabs: (doc.linked_tabs ?? undefined) as ('processing' | 'closing')[] | undefined
      }));
    } catch (error) {
      log.error('Failed to get version history', { error });
      throw error;
    }
  }

  /**
   * Get document counts by channel and category (active only)
   */
  static async getDocumentStats(investorId: string): Promise<{
    wholesale: { total: number; byCategory: Record<string, number> };
    correspondent: { total: number; byCategory: Record<string, number> };
  }> {
    try {
      const { data, error } = await supabase
        .from('investor_documents')
        .select('document_type, category')
        .eq('investor_id', investorId)
        .eq('is_active', true);

      if (error) {
        log.error('Failed to fetch document stats', { error });
        throw new Error(`Failed to fetch document stats: ${error.message}`);
      }

      const stats = {
        wholesale: { total: 0, byCategory: {} as Record<string, number> },
        correspondent: { total: 0, byCategory: {} as Record<string, number> }
      };

      for (const doc of data || []) {
        const channel = doc.document_type as 'wholesale' | 'correspondent';
        stats[channel].total++;
        stats[channel].byCategory[doc.category] = (stats[channel].byCategory[doc.category] || 0) + 1;
      }

      return stats;
    } catch (error) {
      log.error('Failed to get document stats', { error });
      throw error;
    }
  }
}
